/**
 * Date of birth and sex assigned at birth, as confirmed on the PN consultation
 * card (BAR-2928).
 *
 * Sex assigned at birth is the US Core `birthsex` extension, never
 * `Patient.gender`: it feeds clinical rules (the pregnancy question, later
 * dosing), while `gender` stays as the patient describes themselves.
 */

import { Extension, Patient } from '@medplum/fhirtypes';

export const US_CORE_BIRTH_SEX_EXTENSION = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex';

/**
 * The codes every US Core version's birth-sex binding accepts: `F` and `M` from
 * v3 AdministrativeGender, `UNK` from v3 NullFlavor. Later versions add more
 * null flavours (`ASKU`, `OTH`); the card offers only the shared three.
 */
export const BIRTH_SEX_OPTIONS = [
  { code: 'F', label: 'Female' },
  { code: 'M', label: 'Male' },
  { code: 'UNK', label: 'Unknown' },
] as const;
export type BirthSex = (typeof BIRTH_SEX_OPTIONS)[number]['code'];

const BIRTH_SEX_CODES: ReadonlySet<string> = new Set(BIRTH_SEX_OPTIONS.map((option) => option.code));

/** The recorded sex assigned at birth, or undefined when none is recorded. */
export function readBirthSex(patient: Pick<Patient, 'extension'> | undefined): BirthSex | undefined {
  const code = patient?.extension?.find((extension) => extension.url === US_CORE_BIRTH_SEX_EXTENSION)?.valueCode;
  return code && BIRTH_SEX_CODES.has(code) ? (code as BirthSex) : undefined;
}

/**
 * The patient's extensions with sex assigned at birth set to `code`, every other
 * extension untouched and in order. The value replaces the first existing one in
 * place; any further copies are dropped, since the extension is single-valued.
 */
export function withBirthSex(extensions: Extension[] | undefined, code: BirthSex): Extension[] {
  const birthSex: Extension = { url: US_CORE_BIRTH_SEX_EXTENSION, valueCode: code };
  const result: Extension[] = [];
  let placed = false;

  for (const extension of extensions ?? []) {
    if (extension.url !== US_CORE_BIRTH_SEX_EXTENSION) {
      result.push(extension);
    } else if (!placed) {
      result.push(birthSex);
      placed = true;
    }
  }

  if (!placed) result.push(birthSex);
  return result;
}

/** No birth date before this is accepted; anything earlier is a typo. */
export const BIRTH_DATE_EARLIEST = '1900-01-01';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Why `value` cannot be saved as `Patient.birthDate`, or undefined when it can.
 * Accepts a real `YYYY-MM-DD` date from 1900 through today (the editor's local
 * day), inclusive.
 */
export function birthDateError(value: string, today: Date = new Date()): string | undefined {
  const match = ISO_DATE.exec(value);
  if (!match) return 'Enter a date of birth';

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
    return 'Enter a real date';
  }

  if (value < BIRTH_DATE_EARLIEST) return 'Date of birth is before 1900';
  if (value > localIsoDate(today)) return 'Date of birth is in the future';
  return undefined;
}
